import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { AxiosServiceResponse, PagedList } from 'interfaces/general';

import { ProductForTicket } from './product';

const instance = axios.create({
  baseURL: `${process.env.TICKETS_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

const createURLqstring = (
  url: string,
  params: UpToDateTicketsParams & IncidentsAssignedByClientParams
) => {
  const querystring = new URLSearchParams();
  const { TicketStatusIds, AssignedEngineerIds, ClientIds, Text, ClientId } =
    params;
  if (TicketStatusIds?.length) {
    TicketStatusIds?.forEach((id) =>
      querystring.append('TicketStatusIds', String(id))
    );
  }
  if (AssignedEngineerIds)
    querystring.append('AssignedEngineerIds', String(AssignedEngineerIds));
  if (ClientIds) querystring.append('ClientIds', String(ClientIds));
  if (ClientId) querystring.append('ClientId', String(ClientId));
  if (Text) querystring.append('Text', Text);

  const urlToString = `${url}${querystring.toString()}`;
  return urlToString?.toString();
};

/* REQUESTS */

export interface ReincidentParams {
  isExport?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
}

export interface ReincidentParamsByLocID extends ReincidentParams {
  LocationId: number;
  InterventionCode?: number;
  CreateDate?: string;
  CloseDate?: string;
  AssignedEngineer?: string;
}

export interface AcceptAssignationRequest {
  ticketsId?: string[] | null;
  comment?: string | null;
}

export interface RejectAssignationRequest {
  ticketsId?: string[] | null;
  comment?: string | null;
  reasonId?: number;
}

export interface TickestAssignationRequest {
  assignationTaskForce: string | number;
  assignationComment?: string | null;
  ticketsId?: string[] | null;
  remote?: boolean;
}
export interface TickestReAssignationRequest {
  AssignationTaskForce: any;
  assignationComment?: string | null;
  ticketsId?: string[] | null;
}

export interface TicketsGenericRequest {
  ticketsId?: string[] | null;
  assignedTaskForceId?: number | null;
}

export interface SaveTicketRequest {
  productIds?: number[];
  requestedByUserId?: number | string;
  clientId?: number | string;
  changeTypeId?: number;
  changeServiceId?: number;
  ownerSystemUserId?: number;
  isServiceAffected?: boolean;
  title?: string;
  description?: string;
  serviceId?: number;
  engineerId?: number | null;
  towerId?: number | null;
  locationId: number;
  clientJobSubCategoryId?: number;
  ticketClientNumber?: string | null;
  ticketClientGroup?: string | null;
  frequentFailureId?: number | null;
  comment?: string | null;
  itilTranslatedText?: string;
  productId?: number | null;
  replicated?: boolean | null;
  remote?: boolean | null;
  date?: string;
  isMajor?: boolean;
  parentTicketIds?: number[];
  openingFields?: { fieldName: string; text?: string }[];
  RequestedByUserId?: number;
  assignmentPriorityId?: number;
  riskId?: number;
  requesterEmail?: string;
  categoryId?: number | string;
  subcategoryId?: number | string;
  costs?: number;
  currencyId?: number;
  unusualSkills?: string;
  specificTechnologies?: string;
  SystemGroupId?: number | string;
  changeFlowId?: number;
}

export interface TaskForceTicketsSearchRequest {
  pageNumber?: number;
  pageSize?: number;
  ticketId?: string | null;
  clientName?: string | null;
  serviceName?: string | null;
  locationName?: string | null;
  productSerialNumber?: string | null;
  clientTicketNumber?: string | null;
  creationDateFromUTC?: string | null;
  creationDateToUTC?: string | null;
  ticketsStatusIds?: number[] | null;
  orderColumn?: string;
  descending?: boolean;
  fromPercent?: string;
  toPercent?: string;
  wasReassigned?: boolean;
  firstTimeAssigned?: boolean;
  returnColumns?: string[];
  isExport?: boolean;
}

export interface TicketGeneralSearchRequest {
  pageNumber?: number;
  pageSize?: number;
  ticketId?: string | null;
  clientName?: string | null;
  serviceName?: string | null;
  locationName?: string | null;
  productSerialNumber?: string | null;
  clientTicketNumber?: string | null;
  creationDateFromUTC?: string | null;
  creationDateToUTC?: string | null;
  pendingValidationDateFromUTC?: string | null;
  pendingValidationDateToUTC?: string | null;
  historicDateFromUTC?: string | null;
  historicDateToUTC?: string | null;
  assignedEngineerEmail?: string | null;
  ticketsStatusIds?: number[] | null;
  orderColumn?: string;
  descending?: boolean;
  fromPercent?: string;
  toPercent?: string;
  returnColumns?: string[];
  isExport?: boolean;
  remote?: boolean;
  firstTimeAssigned?: boolean;
  wasReassigned?: boolean;
  reportType?: 'PDF' | 'CSV' | 'XLSX';
  reportGuid?: string;
  createdUserIds?: number[] | null;
}

export interface TicketUpdateRequestSchedueled {
  TicketId?: string | null;
  comment?: string | null;
  attentionDate?: Date | string | null;
  dispatcherIds?: number[] | null;
  reasonId?: number | null;
  scheduledApproved?: boolean | null;
}
export interface TicketUpdateRequest {
  latitude?: number;
  longitude?: number;
  comment?: string;
  ticketId?: number;
  finalStatusId?: number;
  firstCallSolution?: boolean;
  assignedEngineerId?: number;
  interventionCodeId?: number;
  ticketLasStatusId?: number;
  pendingReasonStatusId?: number;
  rejectionStatusId?: number;
  cancelationReasonStatusId?: number;
  pendingSerieStatusId?: number;
  warehouseId?: number;
  remote?: boolean;
}

export interface TicketDelay {
  ticketId: number;
  ticketDelayClassificationId: number;
}

export interface TicketUpdateResponse {
  ticketId: string | null;
  isError: boolean;
  isEvaluated: boolean;
  message: string;
  daySchedule: {
    initTime: string;
    endTime: string;
  };
}

export interface TicketUpdateToReOpenedRequest {
  tickets?: {
    ticketId: string;
    reasonIds: number;
  }[];
  comment?: string | null;
}
export interface Accessory {
  stockId: number;
  quantity: number;
}
export interface TicketUpdateToTicketOnSiteRequest {
  ticketId: number[];
  comment: string;
}

export interface TicketUpdateToCancelRequest {
  ticketId: number;
  reasonId: number;
  comment: string;
}

export interface TicketUpdateToCancelResponse {
  ticketId: number;
  message: string;
}

export interface TicketUpdateToPendingValidationAndCloseRequest {
  ticketId: number;
  interventionCodeId: number;
}

export interface TicketUpdateToPendingForAReason {
  ticketId: number;
  reasonId: number;
}

export interface TicketUpdateToPendingReasonStatusResponse {
  isError: boolean;
  isEvaluated: boolean;
  message: string;
  ticketId: number;
}

export interface TicketDataMassiveLoad {
  index: number;
  serviceId: number;
  engineerId: number;
  locationId: number;
  ticketClientNumber: string;
  ticketClientGroup: string;
  frequentFailureId: number;
  comment: string;
  productId: number;
}

export interface MassiveLoadRequest {
  tickets: [TicketDataMassiveLoad];
}

/* RESPONSES */
export interface TicketCreated {
  ticketId: number;
  createdDateTimeUTC: Date | string;
}

export interface CreateRelateableTicket {
  ticketId?: number;
  createdDateTimeUTC?: string;
  message?: string;
}

export interface TicketsRelatedDetail {
  relatedTicketId: number;
  serviceId: number;
  serviceName: string;
  locationId: number;
  locationName: string;
  ticketStatusId: number;
  ticketStatusName: string;
  clientId: number;
  clientName: string;
  serviceTypeId: number;
  serviceTypeName: string;
}
export interface TicketSearched {
  [x: string]: string;
  ticketId: string;
  clientName: string;
  project: string;
  serviceName: string;
  clientTicketNumber: string;
  assignedEngineerEmail: string;
  assignedEngineerFullName: string;
  statusId: number;
  statusName: string;
  locationName: string;
  locationDescription: string;
  addressId: number;
  addressFull: string;
  serviceTypeName: string;
  postalCode: string;
  localityName: string;
  municipalityName: string;
  countryStateName: string;
  latitude: string;
  longitude: string;
  firstCallSolution: 'SI' | 'NO' | 'YES' | 'NO';
  ticketProgress: number;
  flags: {
    steps: {
      statusId?: number;
      percent?: number;
      date?: Date;
    }[];
    ticketProgress: string;
    indicatorHexColor: string;
  };
  scheduledDate: Date;
  fromPercent?: string;
  toPercent?: string;
  wasReassigned?: boolean;
  firstTimeAssigned?: boolean;
  tasksDescription?: string;
  itilTranslatedDescription?: string;
  itilTranslatedCloseComment?: string | null;
  ticketDelayClassificationName?: string;
  returnColumns?: string[];
  comunicationAdvanceSolution: {
    commentId?: number;
    commentTypeName?: string;
    commentTypeId?: number;
    comment?: string;
    userName?: null;
    movementUserName?: null;
    createdDateTime?: string;
    commitmentDate?: null;
  }[];
  creationDateUTC: string;
  startDate: string;
  assignedDateTime: string;
  attendedDate: string;
  closedDate: string;
  cancelledDate: string;
  lastDatePendingAssigned: string;
  lastDatePendingConfirmed: string;
  lastDateAssignedConfirmed: string;
  lastDateTransfer: string;
  lastDateOnSite: string;
  lastDatePendingReason: string;
  lastDatePendingValidation: string;
  lastDateHistoric: string;
  lastDateScheduled: string;
  lastDateReopen: string;
  reassigned: boolean;
  rejected: boolean;
  reopen: boolean;
  ticketCloseFields?: string;
  interventionCode: string;
  interventionCodeName: string;
  interventionCodeId: number;
  externalOrigin: string;
  externalEventId: number[];
  createdUser?: string;
}

export interface TicketStatusTrackingsDTO {
  id: number;
  ticketId: number;
  initialStatusId: number;
  finalStatusId: number;
  ticketCommnentTypeId: null;
  comment: string;
  latitude: null;
  longitude: null;
  finalStatus: null;
  initialStatus: null;
  ticket: null;
  ticketCommnentType: null;
  ticketStatusTrackingReasons: any[];
  createdUserId: number;
  createdUser: null;
  createdDateTime: Date;
}

export interface ITicketProduct {
  productId: number;
  serialNumber: string;
  brand: string;
  model: string;
  modelDescription: string;
  enabled: boolean;
  status: string;
  conditionId: number;
  addedInCreation?: boolean;
  macAddress?: string;
  lastDiagnosticDate?: string | Date;
  ticketProduct?: string;
  lastDiagnosticStatus?: string;
}
export interface TicketDetail {
  ticketId: number;
  ticketStatusId: number;
  ticketStatusName: string;
  requestedByUser: string;
  ticketStatusDescription: string;
  ticketCommentTypeId: number;
  ticketDelayClassificationId?: number;
  ticketComment: {
    id: number;
    ticketCommentTypeId: number;
    comment: string;
  }[];
  ticketProductsId: number[];
  ticketLocationId: number;
  ticketLocationName: string;
  ticketLocationDescription: string;
  ticketLocationCommonName: string;
  ticketLocationPriority: number;
  ticketLocationAddress: string;
  ticketLocationInternalNumber: string;
  ticketLocationExternalNumber: string;
  ticketLocationMunicipality: string;
  ticketLocationPostalCode: string;
  ticketLocationState: string;
  ticketLocationPhoneCode: string;
  ticketLocationPhone: string;
  ticketLocationZone: string;
  ticketLocationLatitude: string;
  ticketLocationLongitude: string;
  ticketLocationObservation: string;
  ticketServiceId: number;
  ticketServiceName: string;
  ticketServiceOperationTypeId: number;
  ticketServiceOperationTypeName: string;
  ticketClientId: number;
  ticketClientName: string;
  ticketServiceTypeId: number;
  ticketServiceTypeName: string;
  ticketResponsibleId: string;
  ticketClientNumber: string;
  ticketCreationDateUTC: Date;
  ticketAssignedEngineerEmail: string;
  ticketAssignedEngineerFullName: string;
  ticketAssignedEngineerDateTime: string;
  ticketFrequentFailureName: string;
  ticketProjectId?: number;
  ticketProjectName?: string;
  ticketProduct: ITicketProduct | ITicketProduct[] | ProductForTicket[];
  flags: {
    steps: {
      [id: string]: number;
    };
    ticketProgress: string | number;
    indicatorHexColor: string;
  };
  ticketStartDate: Date;
  ticketAcceptedDateTime: string;
  ticketScheduledDateTime: Date;
  ticketClosedDateTime: Date;
  ticketWorkingDateTime: Date;
  ticketAttentionTime: Date;
  onSiteTaskDescription: string;
  itilTranslatedDescription?: string;
  itilTranslatedCloseComment?: string | null;
  requireProducts?: boolean;
  requireSupplies?: boolean;
  ticketAssignedEnginnerId?: number;
  ticketServiceTypeClosingFields?: {
    id: number;
    name: string;
    serviceTypeId: number;
    itemOrder: number;
    required: boolean;
    isVisibleInPdf: boolean;
    translateToItilGenius?: boolean;
    /** @deprecated use translateToItilGenius */
    exportToItil?: boolean;
  }[];
  ticketClientGroup: string;
  comunicationAdvanceSolution: {
    commentId?: number;
    commentTypeName?: string;
    commentTypeId?: number;
    comment?: string;
    userName?: null;
    movementUserName?: null;
    createdDateTime?: string;
    commitmentDate?: null;
  }[];
  relatedTicketIds?: number[];
  ticketChilds?: number[];
  ticketPendingValidationDateTime: string;
  ticketOpeningFieldsComment?: string;
  ticketStatusColor?: string;
  ticketActualFailureName?: string;
  ticketStatusCategoryId?: number;
  canBeRelated?: boolean;
  external?: boolean;
  externalEventId?: number[];
  externalOrigin?: string;
  requesterEmail?: string;
  categoryName?: string;
  subcategoryName?: string;
  impactName?: string;
  urgencyName?: string;
  priorityName?: string;
  setDelayClassification?: string;
  towerId?: number;
  towerName?: string;
}

export interface TicketsAssignation {
  message: string;
  isError: boolean;
  isEvaluated: boolean;
  ticketId: string;
}

export interface SystemUser {
  userId: number;
  firstName?: string;
  firstLastName?: string;
  secondLastName?: string;
  fullName?: string;
  email?: string;
  accessTypeId?: number;
  accessTypeName?: string;
}

export interface GetLogbookResponse {
  movNumber: string;
  statusIni: string;
  statusFin: string;
  statusIniId: number;
  statusFinId: number;
  movement: string;
  createDateTime: any;
  userName: string;
  fullName: string;
  latitude: number;
  longitude: number;
  statusIniHexColor: string;
  statusFinHexColor: string;
}

/* COMMET INTERFACE */

export interface GetCommentListByTicketResponse {
  commentId: number;
  commentTypeName: string;
  commentTypeId: number;
  comment: string;
  userName: null;
  commentPosition: null;
  createdDateTime: Date | string;
  commitmentDate: null;
}

export interface GetCommentListByTicketRequest {
  ticketId: number;
  ticketCommentTypeId: number;
  commitmentDate: Date | string;
  comment: string;
}

export type UpdateTicketDescriptionRequest = {
  ticketId: number;
  comment: string;
  itilTranslatedText?: string;
};

export type TicketApiEnvelope<T> = {
  data: T;
  message: string;
  isError: boolean;
};

function unwrapTicketApiEnvelope<T>(
  response: AxiosServiceResponse<TicketApiEnvelope<T>>
): T {
  const body = response.data;
  if (body.isError) {
    throw new Error(body.message || 'Error en la solicitud.');
  }
  return body.data;
}

export interface CommentsDetail {
  problemDetail?: string;
  workDoneDetail?: string;
  fixStatusDetail?: string;
  observationsDetail?: string;
}

export interface ClosedDetailsCraneProviderOption {
  id: number;
  name: string;
}

export interface ClosedDetailsCraneUnitOption {
  id: number;
  label: string;
  craneProviderId?: number;
}

export interface RentalConsumptionDto {
  id: number;
  gserProjectId: number;
  purchaseId: number | null;
  craneId: number | null;
  supplierId: number | null;
  travelHours: number;
  operationHours: number;
  durationHours: number;
  isOtherProvider: boolean;
  rentalServiceTicketId: number | null;
  rentalCranePlate: string | null;
  rentalSupplierName: string | null;
  rentalCorrelationId: string | null;
}

export interface RentalConsumptionLineRequest {
  id?: number;
  gserProjectId: number;
  purchaseId: number;
  craneId?: number | null;
  supplierId?: number | null;
  travelHours: number;
  operationHours: number;
  durationHours: number;
  isOtherProvider: boolean;
}

export interface ClosedDetailsToTicketRequest {
  ticketId?: number;
  commentDetails?: string;
  itilTranslatedCloseComment?: string | null;
  interventionCodeId?: number;
  interventioCode?: string;
  interventionCodeName?: string;
  actualFailureName?: string;
  requiresInterventionCode?: boolean;
  allowsCraneUsage?: boolean;
  /** Fuente operativa multi-consumo */
  rentalConsumptions?: RentalConsumptionDto[];
  /** Legacy — solo compat si el array viene vacío */
  rentalSupplierName?: string;
  rentalCranePlate?: string;
  rentalHoursConsumed?: number;
  rentalPurchaseId?: number;
  rentalCraneId?: number;
  rentalServiceTicketId?: number;
}

/** @deprecated Usar `RentalConsumptionLineRequest` vía `rentalConsumptions`. */
export interface ClosedDetailsRentalCranePayload {
  isOtherProvider: boolean;
  craneId: number;
  durationHours: number;
}

export interface ClosedDetailsToTicketRequestv2 {
  ticketId: number;
  closingFields: {
    fieldName: string;
    text: string;
  }[];
  actualFailureId: number;
  interventionCodeId: number;
  /** Líneas de consumo de grúa (1 a 5). `null` cuando no se registra ninguna. */
  rentalConsumptions: RentalConsumptionLineRequest[] | null;
  itilTranslatedText?: string;
}

export interface GetClosedCommentsToTicket {
  ticketId?: number;
  commentDetails: string;
  itilTranslatedCloseComment?: string | null;
  taskDoneDetail?: string;
  taskToBeDoneDetail?: string;
}

export interface SearchTicketsFlags {
  fromPercent: number;
  toPercent: number;
}

export interface AssignedStatistics {
  acceptedCount: number;
  reAssignedCount: number;
  assignedCount: number;
  rejectedCount: number;
  firstTimeAssigned: number;
}

export interface UpToDateTicketsStatistics {
  onTimeCount: number;
  toExpireCount: number;
  expiredCount: number;
}

export interface ByStatusTicketsStatistics {
  closed: number;
  travel: number;
  working: number;
  assignedAndConfirmed: number;
  pendingConfirmation: number;
  pendingForWarehouse: number;
  pendingReason: number;
  pendingSerie: number;
  revPendingForWarehouse: number;
  revPendingSerie: number;
  scheduled: number;
}

export interface AssignedTickets {
  total: number;
  statistics: AssignedStatistics;
}

export interface UpToDateTickets {
  total: number;
  statistics: UpToDateTicketsStatistics;
}

export interface UpToDateTicketsParams {
  OnlyIncidents?: boolean;
  IsWeb?: boolean;
  TicketStatusIds?: number[];
  AssignedEngineerIds?: number;
  ClientIds?: number;
  Text?: string;
  ServiceIds?: number[] | number;
  ClientId?: never;
}

export interface ServicesAssignedByClient {
  serviceId: number;
  name: string;
  value: number;
}

export interface AssignedByClientStatistics {
  clientId: number;
  name: string;
  value: number;
  services?: ServicesAssignedByClient[];
}

export interface IncidentsAssignedByClient {
  total: number;
  statistics: AssignedByClientStatistics[];
}

export interface IncidentsAssignedByClientParams {
  ClientId?: number;
  Text?: string;
  TicketStatusIds?: number[];
  AssignedEngineerIds?: number;
  ServiceIds?: number[] | number;
}
export interface ByStatusTickets {
  total: number;
  statistics: {
    statuses: {
      statusId: number;
      statusName: string;
      statusCount: number;
      statusColor: string;
    }[];
  };
}

export interface CommenTypesResponse {
  commentTypeId: number;
  commentTypeName: string;
}

export interface ErrorTicket {
  index: number;
  errorMsg: string;
}

export interface Data {
  data: {
    ticketIds?: number[];
    errorTickets?: ErrorTicket[];
    createdDateTimeUTC?: Date;
  };
}

export interface MassiveLoadResponse {
  data?: Data;
  message?: string;
  isError?: boolean;
}

// CARGA MASIVA INTERFASE
export interface TicketsAdded {
  index: number;
  serviceId: number;
  serviceName: string;
  serviceTypeName: string;
  engineerId: number;
  locationId: number;
  locationName: string;
  locationDescription: string;
  ticketClientNumber: string;
  ticketClientGroup: string;
  comment: string;
  productId: number;
  frequentFailureId: number;
  successTicketFolio: number;
  programmed: boolean;
  startDate: string | null;
  createdDate?: string | null;
}

export interface MassiveBulkResponse {
  ticketsAdded: TicketsAdded[];
  errorTickets: ErrorTicket[];
  createdDateTimeUTC: Date;
}

export interface MassiveBulkRequest {
  tickets: BulkLoadTicket[];
  withoutSaving: boolean;
}

export interface BulkLoadTicket {
  index: number;
  clientName: string;
  locationName: string;
  productSerialNumber: string;
  serviceId: number;
  comment: string;
  engineerId: number;
  ticketClientNumber?: string;
  remote: boolean;
  programmed: boolean;
  startDate: Date | string | null;
  RequestedByUserId?: number;
  RequesterEmail?: string | null;
}

export interface AllTicketStatus {
  id: number;
  value: string;
}

export interface TicketsByLocationParams {
  locationId?: number;
  serviceName?: string;
  serviceType?: string;
  serialNumber?: string;
  assignedEngineer?: string;
  ticketStatus?: string;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  text?: string;
  isExport?: boolean;
}

export interface TicketsByLocationResponse {
  ticketId: number;
  serviceType: string;
  service: string;
  serialNumber: string;
  assignedEngineer: string;
  ticketStatus: string;
  closedDate: string;
}

export interface AvailableStatus {
  id: number;
  description: string;
  name: string;
  isInProgressStatus: boolean;
  behaviors: {
    requiresEngineer: boolean;
    requiresCancelationReason: boolean;
    requiresInterventionCode: boolean;
    isFinalStatus: boolean;
    isResolvingTicket: boolean;
    isRejectionStatus: boolean;
    isPendingReasonStatus: boolean;
    isReviewScheduledStatus: boolean;
    isPendingSerieStatus: boolean;
    isPendingWarehouseStatus: boolean;
    requiresDocumentation: boolean;
    isInternalRejectionStatus: boolean;
    isClientRejectionStatus: boolean;
    serviceRequirement: boolean;
  };
}

export interface ClosedDetailsResponse {
  ticketId: number;
  commentsDetail: {
    problemDetail: string;
    workDoneDetail: string;
    fixStatusDetail: string;
    observationsDetail: string;
  };
}

export interface CreateNewMovement {
  products?: Product[];
  ticketId?: number;
}

export interface Product {
  productAdded?: ProductAdded | null;
  productReleased?: ProductReleased | null;
}

export interface ProductAdded {
  productId?: number;
  operationType?: number;
}

export interface ProductReleased {
  productId?: number;
  operationType?: number;
}

export interface CreateNewMovementKits {
  ticketId?: number;
  kitIds?: number[];
}

export interface CreateNewMovementSupplies {
  ticketId?: number;
  supplies?: {
    supplyId: number;
    quantity: number;
  }[];
}

export interface DeleteMovementConfigComponents {
  products?: ProductDelete[];
  ticketId?: number;
}

export interface ProductDelete {
  productAddedToRemove?: ProductEdToRemove | null;
  productReleasedToRemove?: ProductEdToRemove | null;
}

export interface ProductEdToRemove {
  productId?: number;
  operationType?: number;
}

export interface DeleteMovementSupplies {
  ticketId?: number;
  supplies?: {
    supplyId: number;
    quantity: number;
  }[];
}

export interface TaskForceSubstitutionRequest {
  ticketIds: number[];
  userIdSubstitution: number;
}

// Interfaces para cambio masivo de estado
export interface BulkStatusChangeTicket {
  ticketId: number;
  comment: string;
  assignedEngineerId?: number;
  firstCallSolution?: boolean;
  interventionCodeId?: number;
  pendingReasonStatusId?: number;
  rejectionStatusId?: number;
  cancelationReasonStatusId?: number;
  pendingSerieStatusId?: number;
  warehouseId?: number;
  remote?: boolean;
}

export interface BulkStatusChangeRequest {
  finalStatusId: number;
  tickets: BulkStatusChangeTicket[];
}

export interface BulkStatusChangeResponse {
  totalProcessed: number;
  totalSuccess: number;
  totalFailed: number;
  successfulTickets: number[];
  failedTickets: number[];
  allSuccessful: boolean;
  hasErrors: boolean;
  summary: string;
}

export interface UpdateToRevPendingWarehouseRequest {
  ticketId: number;
  comment: string;
  warehouseId: number;
}

export interface UpdateToPendingWarehouseAndSerieRequest {
  latitude?: number;
  longitude?: number;
  ticketId: number;
  comment: string;
  warehouseRevisionApproved?: boolean;
}

export interface UpdateTicketToPendingforSerialStatusPayload {
  ticketId: number;
  comment: string;
}

export interface UpdateToRevPendingForSerieRequest {
  ticketId: number;
  comment: string;
  reasonId: number;
  warehouseId: number;
}

export interface WorkForceLocation {
  coordinates: {
    ticketId: number;
    latitude: number;
    longitude: number;
    date: string;
  }[];
  workForceId: number;
}

type EscalationSpecificParams = {
  time?: string;
  recurringInt?: number;
  recurringTime?: string;
  ticketStatusIds?: number[];
  days?: number;
  distance?: number;
  percentage?: number;
  ticketEmailType?: number;
  emails?: string;
};

export interface TicketEscalationRequest {
  escalationId?: number;
  name?: string;
  escalationTypeId?: number;
  clientIds?: number[];
  serviceIds?: number[];
  workForceIds?: number[];
  recipientIds?: number[];
  toEmail?: boolean;
  userFirst?: boolean;
  userFirstTime?: string;
  enabled?: boolean;
  allClients?: boolean;
  allServices?: boolean;
  allWorkForces?: boolean;
  parameters?: EscalationSpecificParams;
}

export interface EscalationType {
  id: number;
  name: string;
  description: string;
  parameters: EscalationSpecificParams;
}

export interface Escalation {
  enabled: boolean;
  escalationTypeName: string;
  id: number;
  name: string;
}

export interface TicketsHistoryByLocationParams {
  locationId: number;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  text?: string;
  isExport: boolean;
}

export interface TicketHistoryByLocationResponse {
  ticketId: number;
  serviceType: string;
  service: string;
  serialNumbers: [];
  assignedEngineer: string;
  ticketStatus: string;
  advancedSolutionComment: string;
  activitiDescription: string;
  closedDate: Date;
  createDate: Date;
  commentTracking: string[];
  closedDetails: string;
  indicatorHexColor: string;
  trackingProducts: {
    serialNumber: string;
    operationType: string;
  }[];
  interventionCode: string;
  interventionCodeId: number;
  interventionCodeName: string;
  supplies: {
    supplyId: number;
    supplyName: string;
    unitMeasurementName: string;
    quantity: number;
  }[];
}
export interface IReportConfig {
  clientName?: string | null;
  requestedBy?: string | null;
  serviceTitle?: string | null;
  requestedBySignatureTitle?: string | null;
  clientSignatureTitle?: string | null;
  requestedByPosition?: string | null;
  clientPosition?: string | null;
}

interface IGetTicketReportConfigs {
  clientId: number;
  clientName: string;
  id: number;
  reportConfig: string | IReportConfig;
}

export interface ExpenseReportParams {
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
  isExport?: boolean;
  cost?: string;
  location?: string;
  ticketId?: number;
  ticketIds?: number[];
  serviceName?: string;
  clientName?: string;
  creationDateFromUTC?: Date;
  creationDateToUTC?: Date;
  closeDateFromUTC?: Date;
  closeDateToUTC?: Date;
  scheduledDateFromUTC?: Date;
  scheduledDateToUTC?: Date;
  textType?: number;
  reportType?: string;
  reportGuid?: string;
}

export interface ExpenseReportResponse {
  ticketId?: number;
  locationName?: string;
  serviceName?: string;
  serviceCost?: number;
  clientName?: string;
  cost?: number;
  closeComments?: string;
  installDetails?: null;
  creationDate?: Date;
  closeDate?: Date;
  scheduledDate?: Date;
  tasksDescription?: string;
  componentCostList?: ComponentCostList[];
  supplyCostList?: SupplyCostList[];
}

export interface ComponentCostList {
  serialNumber?: string;
  unitCost?: number;
}

export interface SupplyCostList {
  name?: string;
  unitCost?: number;
  quantity?: number;
  totalCost?: number;
}

export interface ReincidentLocation {
  locationId?: number;
  location?: string;
  amountTickets?: number;
  project?: string;
  client?: string;
}

export interface ReincidentTicketByLocationID {
  ticketId?: string;
  serviceType?: string;
  ticketCommentClose?: string;
  activitiDescription?: string;
  assignedEngineer?: string;
  closedDate?: string;
  createDate?: string;
  attention?: string;
  interventionCode?: string;
  assignedEngineerSecondLastName?: string;
  assignedEngineerFirstLastname?: string;
  assignedEngineerFirstName?: string;
}

export interface MajorIncidentsByLocationAndClient {
  ticketId?: number;
  clientId?: number;
  clientName?: string;
  locationId?: number;
  locationName?: string;
  ticketCount?: number;
}

export interface MayorIncidentParams {
  LocationId: number;
  ClientId: number;
  DateTo: string;
  DateFrom: string;
  isExport?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
}

export interface SaveRelatedRequestTicket {
  serviceId?: number;
  engineerId?: number;
  locationId?: number;
  ticketClientNumber?: string;
  ticketClientGroup?: string;
  frequentFailureId?: number;
  comment?: string;
  productId?: number;
  replicated?: boolean;
  remote?: boolean;
  isMajor?: boolean;
  parentTicketIds?: number[];
}

export interface GetRemoteWorkTicketsResponse {
  ticketId?: number;
  createdDateTime?: Date;
  closedDateTime?: Date;
  problemDetail?: string;
  workDoneDetail?: string;
  assignedEngineer?: string;
  pendingValidationUser?: string;
  clientId?: number;
  clientName?: string;
  projectId?: number;
  projectName?: string;
}

export interface GetRemoteWorkTicketsParams {
  ticketCreationDateTime?: string;
  ticketClosedDateTime?: string;
  projectId?: number;
  projectName?: string;
  clientId?: number;
  clientName?: string;
  isExport?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  pageSize?: number;
  text?: string;
}

export interface EvidenceResponse {
  id?: string;
  description: string;
  isRequired: boolean;
  added: boolean;
}

export interface FirstCallStatistics {
  total: number;
  statistics: {
    firstCallCount: number;
    atentionAverageSeconds: number;
    atentionAverageString: string;
    fakeCount: number;
    realCount: number;
  };
}

export interface ITicketProblemsParams extends ReincidentParams {
  ServiceTypeId?: number;
  CreatedDateTimeFrom?: Date | string;
  CreatedDateTimeTo?: Date | string;
  ExpirationDateTimeFrom?: Date | string;
  ExpirationDateTimeTo?: Date | string;
  problemId?: number;
}

export interface ITicketProblemResponse {
  id?: number;
  ticketId: number;
  requestedByUserId: number;
  requestedByUserName: string;
  assignedEngineerId: number;
  assignedEngineerName: string;
  attentionExpirationDateTime: Date | string;
  ticketStatusId: number;
  ticketStatusName: string;
  createdDateTime: Date | string;
}

export interface RentalCraneCatalogForTicketClose {
  cranes?: Crane[];
}

export interface Crane {
  craneId?: number;
  plateNumber?: string;
  supplierId?: number;
  supplierName?: string;
  status?: string;
  availableHours?: number;
  lastPurchaseDate?: Date;
}

/* API */
export default {
  instance,
  assignations: {
    /**
     * No description
     *
     * @tags Assignations
     * @name getAssignableTaskForceByClientIdDetail
     * @request GET:/api/v1/Assignations/getAssignableTaskForceByClientId/{clientId}
     * @secure
     */
    getAssignableTaskForceByClientIdDetail: (
      clientId: number,
      params?: { locationId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<SystemUser[]>>({
        url: `/Assignations/getAssignableTaskForceByClientId/${clientId}`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * No description
     *
     * @tags Assignations
     * @name getAssignableTaskForceByTicketsId
     * @request POST:/api/v1/Assignations/getAssignableTaskForceByTicketsId
     * @secure
     */
    getAssignableTaskForceByTicketsId: (
      data: TicketsGenericRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<SystemUser[]>>({
        url: `/Assignations/getAssignableTaskForceByTicketsId`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags Assignations
     * @name assignTaskForceToTicketsCreate
     * @request POST:/api/v1/Assignations/assignTaskForceToTickets
     * @secure
     */
    assignTaskForceToTicketsCreate: (
      data: TickestAssignationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketsAssignation[]>>({
        url: `/Assignations/assignTaskForceToTickets`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags Assignations
     * @name reAssignTaskForceToTicketsCreate
     * @request POST:/api/v1/Assignations/reAssignTaskForceToTickets
     * @secure
     */
    reAssignTaskForceToTicketsCreate: (
      data: TickestAssignationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketsAssignation[]>>({
        url: `/Assignations/reAssignTaskForceToTickets`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * No description
     *
     * @tags Assignations
     * @name reAssignTaskForceToTicketsReOpened
     * @request POST:/api/v1/Assignations/reAssignTaskForceToTicketsReOpened
     * @secure
     */
    reAssignTaskForceToTicketsReOpened: (
      data: TickestReAssignationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketsAssignation[]>>({
        url: `/Assignations/reAssignTaskForceToTicketsReOpened`,
        method: 'POST',
        data,
        ...config
      })
  },
  taskForce: {
    /**
     * ACTUALIZA EL TICKET A "ACEPTADO POR FUERZA DE TRABAJO"
     *
     * @tags TaskForce
     * @name meAcceptAssignations
     * @request POST:/api/v1/Tickets/acceptAssignations
     * @secure
     */
    meAcceptAssignations: (
      data: AcceptAssignationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, any>({
        url: `/Tickets/acceptAssignations`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "RECHAZADO POR FUERZA DE TRABAJO"
     *
     * @tags TaskForce
     * @name meRejectAssignations
     * @request POST:/api/v1/TaskForce/me/rejectAssignations
     * @secure
     */
    meRejectAssignations: (
      data: RejectAssignationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, any>({
        url: `/TaskForce/me/rejectAssignations`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags TaskForce
     * @name meSearchMyTicktesCreate
     * @request POST:/api/v1/TaskForce/me/searchMyTicktes
     * @secure
     */
    meSearchMyTicktes: (
      data: TaskForceTicketsSearchRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<TicketSearched>>>({
        url: `/TaskForce/me/searchMyTicktes`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Cambiar de ingeniero asignado en ticket
     *
     * @tags TaskForce
     * @name taskForceSubstitution
     * @request POST:/api/v1/TaskForce/me/TaskForceSubstitution
     * @secure
     */
    taskForceSubstitution: (
      data: TaskForceSubstitutionRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/TaskForce/me/TaskForceSubstitution`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtener las geolocalizaciones de la fuerza de trabajo
     *
     * @tags TaskForce
     * @name getGeolocations
     * @request GET:/api/v1/TaskForce/Geolocations
     * @secure
     */
    getGeolocations: (
      params: { requestUserId: number; fromDate: string; toDate: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<WorkForceLocation[]>>({
        url: `/TaskForce/Geolocations`,
        method: 'GET',
        params,
        ...config
      })
  },
  tickets: {
    /**
     * No description
     *
     * @tags Tickets
     * @name create
     * @request POST:/api/v1/Tickets/SaveTicket
     * @secure
     */
    create: (data: SaveTicketRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TicketCreated>>({
        url: `/Tickets`,
        method: 'POST',
        ...(data.date
          ? {
              headers: {
                'Content-Type': 'application/json',
                'Request-Date': data.date
              }
            }
          : {}),
        data,
        ...config
      }),
    /**
     * Crea tickets de incidentes Majores relacionado a otro ticket
     *
     * @tags Tickets
     * @name createMayorIncidents
     * @request POST:/api/v1/Tickets/CreateMajorTicket
     * @secure
     */
    createMayorIncidents: (
      data: SaveTicketRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<CreateRelateableTicket>>({
        url: `/Tickets/CreateMajorTicket`,
        method: 'POST',
        ...(data.date
          ? {
              headers: {
                'Content-Type': 'application/json',
                'Request-Date': data.date
              }
            }
          : {}),
        data,
        ...config
      }),
    /**
     * Crea tickets de incidentes relacionado a otro ticket de servicio Solicitudes
     *
     * @tags Tickets
     * @name createRelatedRequest
     * @request POST:/api/v1/Tickets/CreateIncidentRelatedTicket
     * @secure
     */
    createRelatedRequest: (
      data: SaveRelatedRequestTicket,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<CreateRelateableTicket>>({
        url: `/Tickets/CreateIncidentRelatedTicket`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene los tickets asociados a un ticket
     *
     * @tags Tickets
     * @name getTicketsRelatedByTicketId
     * @request GET:/api/v1/Tickets/GetTicketsRelatedByTicketId
     * @secure
     */
    getTicketsRelatedByTicketId: (
      params: { ticketId: number; isChild?: boolean },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketsRelatedDetail[]>>({
        url: `/Tickets/GetTicketsRelatedByTicketId`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TRAE TODOS LOS TICKETS PAGINADOS
     *
     * @tags Tickets
     * @name generalSearch
     * @request POST:/api/v1/Tickets/GeneralSearch
     * @secure
     */
    generalSearch: (
      data: TicketGeneralSearchRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<TicketSearched>>>({
        url: `/Tickets/GeneralSearch`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * TRAE EL DETALLE DEL TICKET POR ID
     *
     * @tags Tickets
     * @name Detail
     * @request GET:/api/v1/Tickets/{id}
     * @secure
     */
    detail: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TicketDetail>>({
        url: `/Tickets/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * No description
     *
     * @tags TaskForce
     * @name updateTicketsToReOpenedStatus
     * @request POST:/api/v1/Tickets/updateTicketStatus
     * @secure
     */
    updateTicketStatus: (
      data: TicketUpdateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ ticketId: number; message?: string }>
      >({
        url: `/Tickets/updateTicketStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags TaskForce
     * @name updateTicketsToReOpenedStatus
     * @request POST:/api/v1/Tickets/updateTicketsToReOpenedStatus
     * @secure
     */
    updateTicketsToReOpenedStatus: (
      data: TicketUpdateToReOpenedRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, any>({
        url: `/Tickets/updateTicketsToReOpenedStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags TaskForce
     * @name updateTicketToPendingAssignment
     * @request POST:/api/v1/Tickets/UpdateTicketToPendingAssignmentStatus
     * @secure
     */
    updateTicketToPendingAssignment: (
      data: { ticketId: number; comment: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/UpdateTicketToPendingAssignmentStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "EN SITIO"
     *
     * @tags TaskForce
     * @name meUpdateTicketOnSite
     * @request POST:/api/v1/Tickets/updateTicketsToOnSiteStatus
     * @secure
     */
    updateTicketsToOnSite: (
      data: TicketUpdateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse[]>>({
        url: `/Tickets/updateTicketsToOnSiteStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "CERRADO"
     *
     * @tags TaskForce
     * @name meUpdateTicketsToOnClosedStatus
     * @request POST:/api/v1/Tickets/updateTicketToClosedStatus
     * @secure
     */
    meUpdateTicketsToOnClosedStatus: (
      ticketId: string,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/updateTicketToClosedStatus`,
        method: 'POST',
        data: { ticketId },
        ...config
      }),

    /**
     * No description
     *
     * @tags Tickets
     * @name updateTicketsToHistoricStatus
     * @request POST:/api/v1/Tickets/me/updateTicketsToHistoricStatus
     * @secure
     */
    meUpdateTicketsToHistoricStatus: (
      data: TicketUpdateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse[]>>({
        url: `/Tickets/updateTicketsToHistoricStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags Tickets
     * @name updateTicketsToCancelStatus
     * @request POST:/api/v1/Tickets/updateTicketsToCancelStatus
     * @secure
     */
    meUpdateTicketsToCancelStatus: (
      data: TicketUpdateToCancelRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<TicketUpdateToCancelResponse>
      >({
        url: `/Tickets/UpdateTicketsToCancelStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * update tickets to "pending validation and close"
     * @tags Tickets
     * @name updateTicketsToPendingValidationAndCloseStatus
     * @request POST:/api/v1/Tickets/UpdateTicketsToPendingValidationAndCloseStatus
     * @secure
     */
    updateTicketsToPendingValidationAndCloseStatus: (
      data: TicketUpdateToPendingValidationAndCloseRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/UpdateTicketsToPendingValidationAndCloseStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * update ticket to pending reason status
     * @tags Tickets
     * @name updateTicketToPendingReasonStatus
     * @request POST:/api/v1/Tickets/updateTicketToPendingReasonStatus
     * @secure
     */
    updateTicketToPendingReasonStatus: (
      data: TicketUpdateToPendingForAReason,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<TicketUpdateToPendingReasonStatusResponse>
      >({
        url: `/Tickets/updateTicketToPendingReasonStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /** update ticket pending confirmation
     * @tags Tickets
     * @name updateTicketToPendingConfirmationStatus
     * @request POST:/api/v1/Tickets/updateTicketToPendingConfirmationStatus
     * @secure
     */
    updateTicketToPendingConfirmationStatus: (
      data: { ticketId: number; comment: string; remote?: boolean },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ ticketId: number; message: string }>
      >({
        url: `/Tickets/updateTicketToPendingConfirmationStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /** update ticket to rejected for non compliance
     * @tags Tickets
     * @name updateTicketsToRejectedForNonComplianceStatus
     * @request POST:/api/v1/Tickets/UpdateTicketsToRejectedForNonComplianceStatus
     * @secure
     */
    updateTicketsToRejectedForNonComplianceStatus: (
      data: { ticketId: number; comment: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ ticketId: number; message: string }>
      >({
        url: `/Tickets/UpdateTicketsToRejectedForNonComplianceStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /** update ticket to quick recover status
     * @tags Tickets
     * @name updateTicketToQuickRecoverStatus
     * @request POST:/api/v1/Tickets/updateTicketToQuickRecoverStatus
     * @secure
     */
    updateTicketToQuickRecoverStatus: (
      data: { ticketId: number; comment: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ ticketId: number; message: string }>
      >({
        url: `/Tickets/updateTicketToQuickRecoverStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Guardar los Ticket de Carga Masiva
     *
     * @tags Tickets
     * @name sendMassiveLoad
     * @request POST:/api/v1/Tickets/SaveMultiTicket
     * @secure
     */
    sendMassiveLoad: (
      data: MassiveLoadRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, MassiveLoadResponse>({
        url: `/Tickets/SaveMultiTicket`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Guardar los Ticket de Carga Masiva por archivo externo
     *
     * @tags Tickets
     * @name sendMassiveBuckByExternalFile
     * @request POST: /api/v1/Tickets/BulkLoadTickets
     * @secure
     */
    sendMassiveBuckByExternalFile: (
      data: MassiveBulkRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<MassiveBulkResponse>>({
        url: `/Tickets/BulkLoadTickets`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Tickets by Location
     * @tags Tickets
     * @name getTicketsByLocation
     * @request GET:/api/v1/Tickets/TicketsByLocation
     * @secure
     */
    getTicketsByLocation: (
      params: TicketsByLocationParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<TicketsByLocationResponse>>
      >({
        url: `/Tickets/TicketsByLocation`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Tickets Available Statuses
     * @tags Tickets
     * @name getAvailableStatuses
     * @request GET:/api/v1/Tickets/{id}/AvailableStatuses
     * @secure
     */
    getAvailableStatuses: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<AvailableStatus[]>>({
        url: `/Tickets/${id}/AvailableStatuses`,
        method: 'GET',
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "EN VIAJE"
     *
     * @tags TaskForce
     * @name meUpdateTicketsToTravelStatus
     * @request POST:/api/v1/Tickets/updateTicketsToTravelStatus
     * @secure
     */
    meUpdateTicketsToTravelStatus: (
      data: TicketUpdateRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse[]>>({
        url: `/Tickets/updateTicketsToTravelStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA LA DEMORA DEL TICKET
     *
     * @tags Demora de ticket
     * @name updateDelayTicket
     * @request POST:/api/v1/Tickets/updateTicketDelayClassificationId
     * @secure
     */
    updateDelayTicket: (data: TicketDelay, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse[]>>({
        url: `/Tickets/updateTicketDelayClassificationId`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "AGENDADO"
     *
     * @tags TaskForce
     * @name meUpdateTicketsToSchedueled
     * @request POST:/api/v1/Tickets/updateTicketToScheduledStatus
     * @secure
     */
    meUpdateTicketsToSchedueled: (
      data: TicketUpdateRequestSchedueled,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/updateTicketToScheduledStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "REVISIÓN PENDIENTE POR SERIE"
     *
     * @tags Ticket
     * @name updateTicketToRevPendingForSerieStatus
     * @request POST:/api/v1/Tickets/updateTicketToRevPendingSerieStatus
     * @secure
     */
    updateTicketToRevPendingForSerieStatus: (
      data: UpdateToRevPendingForSerieRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/updateTicketToRevPendingSerieStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "PENDIENTE POR SERIE"
     *
     * @tags Ticket
     * @name updateTicketToPendingForSerieStatus
     * @request POST:/api/v1/Tickets/updateTicketToPendingSerieStatus
     * @secure
     */
    updateTicketToPendingForSerieStatus: (
      data: UpdateToPendingWarehouseAndSerieRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/updateTicketToPendingSerieStatus`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * ACTUALIZA EL TICKET A "REVISIÓN PENDIENTE POR ALMACEN"
     *
     * @tags Ticket
     * @name updateTicketToRevPendingWarehouseStatus
     * @request POST:/api/v1/Tickets/updateTicketToRevPendingWarehouseStatus
     * @secure
     */
    updateTicketToRevPendingWarehouseStatus: (
      data: UpdateToRevPendingWarehouseRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/updateTicketToRevPendingWarehouseStatus`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * ACTUALIZA EL TICKET A "PENDIENTE POR ALMACEN"
     *
     * @tags Ticket
     * @name updateTicketToPendingWarehouseStatus
     * @request POST:/api/v1/Tickets/updateTicketToPendingWarehouseStatus
     * @secure
     */
    updateTicketToPendingWarehouseStatus: (
      data: UpdateToPendingWarehouseAndSerieRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketUpdateResponse>>({
        url: `/Tickets/updateTicketToPendingWarehouseStatus`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * btiene un historial de Tickets segun la Ubicación
     * @tags Tickets
     * @name getTicketsHistoryByLocation
     * @request GET:/api/v1/Tickets/TicketsHistoryByLocation
     * @secure
     */
    getTicketsHistoryByLocation: (
      params: TicketsHistoryByLocationParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<TicketHistoryByLocationResponse>>
      >({
        url: `/Tickets/TicketsHistoryByLocation`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Confirma un ticket como cerrado por el cliente
     * @tags Tickets
     * @name confirmTicketClosed
     * @request GET:/api/v1/Tickets/ConfirmTicketClosed
     */
    confirmTicketClosed: (token: string, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Tickets/ConfirmTicketClosed`,
        method: 'GET',
        params: {
          token
        },
        ...config
      }),
    /**
     * Obtiene los configuraciones para los reportes de Ticket por Cliente
     * @tags Tickets
     * @name GetTicketReportConfigs
     * @request GET:/api/v1/Tickets/GetTicketReportConfigs
     * @secure
     */
    getTicketReportConfigs: (
      params: { clientId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IGetTicketReportConfigs>>({
        url: `/Tickets/GetTicketReportConfigs`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Guarda las configuraciones para los reportes de Ticket por Cliente
     * @tags Tickets
     * @name SaveTicketReportConfig
     * @request POST:/api/v1/Tickets/SaveTicketReportConfig
     * @secure
     */
    saveTicketReportConfig: (
      data: { clientId: number; reportConfig: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<TicketUpdateResponse>>
      >({
        url: `/Tickets/SaveTicketReportConfig`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene todos los tickets de tipo solicitud para vincular a un problema
     * @tags Tickets
     * @name GetProblemTickets
     * @request GET:/api/v1/Tickets/GetProblemTickets
     * @secure
     */
    GetProblemTickets: (
      params: ITicketProblemsParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ITicketProblemResponse>>
      >({
        url: `/Tickets/GetProblemTickets`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Actualiza el estado de múltiples tickets de forma masiva (proceso asíncrono)
     * Los resultados se envían por email al usuario
     *
     * @tags Tickets
     * @name updateMultipleTicketsStatus
     * @request POST:/api/v1/Tickets/updateMultipleTicketsStatus
     * @secure
     */
    updateMultipleTicketsStatus: (
      data: BulkStatusChangeRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<BulkStatusChangeResponse>>({
        url: `/Tickets/updateMultipleTicketsStatus`,
        method: 'POST',
        data,
        ...config
      })
  },
  closedDetails: {
    // TODO: Eliminar cuando se elimine `src/pages/Ticket/Detail/components/CloseTabBackup`

    /**
     * No description
     *
     * @tags TaskForce
     * @name AddClosedDetailsToTicketCreate
     * @request POST:/api/v1/ClosedDetails/AddClosedDetailsToTicket
     * @secure
     */
    AddClosedDetailsToTicket: (
      data: ClosedDetailsToTicketRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ClosedDetailsToTicketRequest>
      >({
        url: `/ClosedDetails/AddClosedDetailsToTicket`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * No description
     *
     * @tags TaskForce
     * @name AddClosedDetailsToTicketCreate
     * @request POST:/api/v1/ClosedDetails/AddClosedDetailsToTicket
     * @secure
     */
    AddClosedDetailsToTicketV2: (
      data: ClosedDetailsToTicketRequestv2,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ClosedDetailsToTicketRequestv2>
      >({
        url: `/ClosedDetails/AddClosedDetailsToTicket`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtener los detalles de los comentarios de un ticket
     *
     * @tags closedDetails
     * @name GetTicketClosedDetails
     * @request GET /api/v1/ClosedDetails/GetTicketClosedDetails
     * @secure
     */
    GetTicketClosedDetails: (
      params: { ticketId: number | string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ClosedDetailsToTicketRequest>
      >({
        url: `/ClosedDetails/GetTicketClosedDetails`,
        method: 'GET',
        params,
        ...config
      })
  },
  logbook: {
    /**
     * No description
     *
     * @tags Comments
     * @name list
     * @request GET:/api/v1/TicketLogs/GetTicketLog
     * @secure
     */
    list: (
      params: {
        ticketId?: number;
        text?: string;
        orderColumn?: string;
        descending?: boolean;
        pageSize?: number;
        pageNumber?: number;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<GetLogbookResponse>>
      >({
        url: `TicketLogs/GetTicketLog`,
        method: 'GET',
        params,
        ...config
      })
  },
  comments: {
    /**
     * No description
     *
     * @tags Comments
     * @name list
     * @request GET:/api/v1/Comments/GetCommentListByTicket
     * @secure
     */
    list: (
      params: {
        ticketId?: number;
        text?: string;
        orderColumn?: string;
        descending?: boolean;
        pageSize?: number;
        pageNumber?: number;
        NoInternal?: boolean;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<GetCommentListByTicketResponse>>
      >({
        url: 'Comments/GetCommentListByTicket',
        method: 'GET',
        params,
        ...config
      }),
    /**
     * No description
     *
     * @tags Comments
     * @name create
     * @request POST:/api/v1/Comments
     * @secure
     */
    create: (
      data: GetCommentListByTicketRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, any>({
        url: 'Comments/AddCommentToTicket',
        method: 'POST',
        data,
        ...config
      }),
    /**
     * E.P QUE TRAE LOS COMENTARIOS DEL TICKET CUANDO SE PASA A ESTADO CERRADO.
     * Usado en pantalla de cierre y visualización (sin filtrar).
     *
     * @tags Comments
     * @name listCommentsToClose
     * @request GET:/api/v1/ClosedDetails/GetTicketClosedDetails
     * @secure
     */
    listCommentsToClose: (
      params: {
        ticketId?: number;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<GetClosedCommentsToTicket>>({
        url: 'ClosedDetails/GetTicketClosedDetails',
        method: 'GET',
        params,
        ...config
      }),
    /**
     * E.P que trae los tipos tipos de comentarios para la bitacora de comentarios
     *
     * @tags Comments
     * @name getCommenTypes
     * @request GET:/ticket/api/v1/Comments/GetCommentTypes
     * @secure
     */
    getCommenTypes: (
      params: {
        commentTypeIds?: number;
        text?: string;
        orderColumn?: string;
        descending?: boolean;
        pageSize?: number;
        pageNumber?: number;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<CommenTypesResponse>>
      >({
        url: 'Comments/GetCommentTypes',
        method: 'GET',
        params,
        ...config
      }),

    /**
     * E.P que trae nota interna y avance y solucion como tipos de comentarios
     *
     * @tags Comments
     * @name getCommenTypesForTicket
     * @request GET:/ticket/api/v1/Comments/CommentTypesForTicket
     * @secure
     */
    getCommenTypesForTicket: (config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<CommenTypesResponse>>
      >({
        url: 'Comments/CommentTypesForTicket',
        method: 'GET',
        ...config
      }),

    /**
     * Actualiza la descripción de creación del ticket (comentario Creacion/Information).
     *
     * @tags Comments
     * @request PUT:/api/v1/Comments/UpdateTicketDescription
     * @secure
     */
    updateTicketDescription: async (
      data: UpdateTicketDescriptionRequest,
      config: AxiosRequestConfig = {}
    ): Promise<{ message: string }> => {
      const response = await instance.request<
        UpdateTicketDescriptionRequest,
        AxiosServiceResponse<TicketApiEnvelope<{ message: string }>>
      >({
        url: 'Comments/UpdateTicketDescription',
        method: 'PUT',
        data,
        ...config
      });
      return unwrapTicketApiEnvelope(response);
    }
  },
  dashboard: {
    /**
     * Me trae los datos de los graph en el dashboard
     *
     * @tags Dashboard
     * @name list
     * @request GET:/api/v1/Dashboard/GetTicketAssignmentStatistics
     * @secure
     */
    getAssignment: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<AssignedTickets>>({
        url: `/Dashboard/GetTicketAssignmentStatistics`,
        method: 'GET',
        ...config
      }),
    upToDate: (
      params: UpToDateTicketsParams,
      config: AxiosRequestConfig = {}
    ) => {
      const parameters = { ...params };
      if (parameters?.ClientId) {
        parameters.ClientIds = parameters.ClientId;
        delete parameters.ClientId;
      }
      return instance.request<void, AxiosServiceResponse<UpToDateTickets>>({
        url: createURLqstring(
          '/Dashboard/GetTicketUpToDateStatistics?',
          parameters
        ),
        method: 'GET',
        params: { IsWeb: true, OnlyIncidents: true },
        ...config
      });
    },
    getByStatus: (
      params: UpToDateTicketsParams,
      config: AxiosRequestConfig = {}
    ) => {
      const parameters = { ...params };
      if (parameters?.ClientId) {
        parameters.ClientIds = parameters.ClientId;
        delete parameters.ClientId;
      }
      return instance.request<void, AxiosServiceResponse<ByStatusTickets>>({
        url: createURLqstring(
          '/Dashboard/GetTicketByStatusStatistics?',
          parameters
        ),
        method: 'GET',
        params: { IsWeb: true, OnlyIncidents: true },
        ...config
      });
    },

    /**
     * Obtiene un listado de incidentes por Clientes
     *
     * @tags Dashboard
     * @name getIncidentsByClients
     * @request GET:/api/v1/Dashboard/IncidentsByClients
     * @secure
     */
    getIncidentsByClients: (
      params?: IncidentsAssignedByClientParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<IncidentsAssignedByClient>>({
        url: createURLqstring(
          '/Dashboard/IncidentsByClients?',
          params as UpToDateTicketsParams & IncidentsAssignedByClientParams
        ),
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene estadisticas de tickets resueltos en primera llamada
     *
     * @tags Dashboard
     * @name getTicketsFirstCallStatistics
     * @request GET:/api/v1/Dashboard/GetTicketsFirstCallStatistics
     * @secure
     */

    getTicketsFirstCallStatistics: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<FirstCallStatistics>>({
        url: '/Dashboard/GetTicketsFirstCallStatistics',
        method: 'GET',
        ...config
      })
  },
  workFlow: {
    /**
     * TRAE LOS ESTADOS TOTALES DE LOS TICKETS
     *
     * @tags status
     * @name getAllStatus
     * @request GET: /api/v1/TicketStatus
     * @secure
     */
    getAllStatus: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<AllTicketStatus[]>>({
        url: '/TicketStatus',
        method: 'GET',
        ...config
      })
  },
  movements: {
    /**
     * Post para el envio de un movimiento
     *
     * @tags Movimientos
     * @name CreateMovement
     * @request POST :/api/v1/TicketComponents/ConfigureTicketProduct
     * @secure
     */
    CreateMovement: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<CreateNewMovement>>({
        url: `/TicketComponents/ConfigureTicketProduct`,
        method: 'POST',
        ...config
      }),
    /**
     * Post para el envio de un movimiento de Kit
     *
     * @tags Movimientos
     * @name CreateMovementKits
     * @request POST :/api/v1/TicketComponents/ConfigureKitsToTicket
     * @secure
     */
    CreateMovementKits: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<CreateNewMovementKits>>({
        url: `/TicketComponents/ConfigureKitsToTicket`,
        method: 'POST',
        ...config
      }),
    /**
     * Post para el envio de un movimiento de los Insumos(Supplies)
     *
     * @tags Movimientos
     * @name CreateMovementSupplies
     * @request POST :/api/v1/TicketComponents/AddSuppliesToTicket
     * @secure
     */
    CreateMovementSupplies: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<CreateNewMovementSupplies>>({
        url: `/TicketComponents/AddSuppliesToTicket`,
        method: 'POST',
        ...config
      }),
    /**
     * Delete para Componentes de Configuracion
     *
     * @tags Movimientos
     * @name DeleteMovementConfigCompo
     * @request DELETE :/api/v1//TicketComponents/RemoveProductsFromTicket
     * @secure
     */
    DeleteMovementConfigCompo: (
      data: DeleteMovementConfigComponents,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/TicketComponents/RemoveProductsFromTicket`,
        method: 'DELETE',
        data,
        ...config
      }),
    /**
     * Delete para Componentes de Configuracion
     *
     * @tags Movimientos
     * @name DeleteMovementSupplies
     * @request DELETE :/api/v1/TicketComponents/RemoveSuppliesFromTicket
     * @secure
     */
    DeleteMovementSupplies: (
      data: DeleteMovementSupplies,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/TicketComponents/RemoveSuppliesFromTicket`,
        method: 'DELETE',
        data,
        ...config
      })
  },
  ODT: {
    /**
     * Sincroniza la ODT con los Tickets
     *
     * @tags ODT
     * @name sincroniceODT
     * @request POST:/api/v1/TicketsOrder
     * @secure
     */
    sincroniceODT: (
      data: { workFlowOrderId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<{ message: string }>>({
        url: `/TicketsOrder`,
        method: 'POST',
        data,
        ...config
      })
  },
  ticketEscalation: {
    /**
     * Obtiene los escalamiento de ticket
     *
     * @tags TicketEscalation
     * @name GetEscalations
     * @request GET:/api/v1/TicketEscalation/GetEscalations
     * @secure
     */
    GetEscalations: (
      params: {
        text?: string;
        orderColumn?: string;
        descending?: boolean;
        pageSize?: number;
        pageNumber?: number;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<Escalation>>>({
        url: `/TicketEscalation/GetEscalations`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Obtiene los escalamiento de ticket
     *
     * @tags TicketEscalation
     * @name GetEscalationTypes
     * @request GET:/api/v1/TicketEscalation/GetEscalationTypes
     * @secure
     */
    GetEscalationTypes: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<EscalationType[]>>({
        url: `/TicketEscalation/GetEscalationTypes`,
        method: 'GET',
        ...config
      }),
    /**
     * Crea un nuevo escalamiento de ticket
     *
     * @tags TicketEscalation
     * @name CreateEscalation
     * @request POST: /api/v1/TicketEscalation
     * @secure
     */
    CreateEscalation: (
      data: TicketEscalationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketEscalationRequest>>({
        url: `/TicketEscalation`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Actualiza un escalamiento por su id
     *
     * @tags TicketEscalation
     * @name UpdateEscalation
     * @request PUT: /api/v1/TicketEscalation
     * @secure
     */
    UpdateEscalation: (
      data: TicketEscalationRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<TicketEscalationRequest>>({
        url: `/TicketEscalation`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * Obtiene un escalamiento por su Id
     * @name GetEscalationById
     * @request DELETE: /api/v1/TicketEscalation/{id}
     * @secure
     */
    GetEscalationById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TicketEscalationRequest>>({
        url: `/TicketEscalation/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Elimina un escalamiento por su Id
     * @name DeleteEscalationById
     * @request DELETE: /api/v1/TicketEscalation/{id}
     * @secure
     */
    DeleteEscalationById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/TicketEscalation/${id}`,
        method: 'DELETE',
        ...config
      })
  },
  expenseReport: {
    /**
     * Obtiene los datos del reporte de costos
     *
     * @tags expenseReport
     * @name GetExpenseReport
     * @request POST:/api/v1/Costs/Costs
     * @secure
     */
    GetExpenseReport: (
      params: ExpenseReportParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ExpenseReportResponse>>
      >({
        url: `/Costs/Costs`,
        method: 'POST',
        data: params,
        ...config
      })
  },
  recidivistsReports: {
    /**
     * Me trae los reportes reincidentes por Ubicacion
     *
     * @tags Reports
     * @name reincidentLocation
     * @request GET:/api/v1/Dashboard/ReincidentLocation
     * @secure
     */
    reincidentLocation: (
      params: ReincidentParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ReincidentLocation>>
      >({
        url: `/Dashboard/ReincidentLocation`,
        method: 'GET',
        params,
        paramsSerializer: (param) =>
          qs.stringify(param, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * Me trae los reportes reincidentes por Ubicacion
     * @tags Reports
     * @name reincidentTicketByLocationID
     * @request GET:/api/v1/Dashboard/ReincidentTicketsByLocation
     * @secure
     */
    reincidentTicketByLocationID: (
      params: ReincidentParamsByLocID,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<ReincidentTicketByLocationID>>
      >({
        url: `/Dashboard/ReincidentTicketsByLocation`,
        method: 'GET',
        params,
        paramsSerializer: (param) =>
          qs.stringify(param, { arrayFormat: 'repeat' }),
        ...config
      })
  },
  mayorIncident: {
    /**
     * Me trae los Incidentes Mayores
     * @tags Reports
     * @name MajorIncidentsByLocationAndClient
     * @request GET:/api/v1/Dashboard/MajorIncidentsByLocationAndClient
     * @secure
     */
    MajorIncidentsByLocationAndClient: (
      params: MayorIncidentParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<MajorIncidentsByLocationAndClient>>
      >({
        url: `/Dashboard/MajorIncidentsByLocationAndClient`,
        method: 'GET',
        params,
        paramsSerializer: (param) =>
          qs.stringify(param, { arrayFormat: 'repeat' }),
        ...config
      })
  },
  remoteWorkReport: {
    /**
     * Me trae los Tickets de Trabajo Remoto
     * @tags Reports
     * @name GetRemoteWorkTickets
     * @request GET: api/v1/Dashboard/GetRemoteWorkTickets
     * @secure
     */
    GetRemoteWorkTickets: (
      params: GetRemoteWorkTicketsParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<GetRemoteWorkTicketsResponse>>
      >({
        url: `/Dashboard/GetRemoteWorkTickets`,
        method: 'GET',
        params,
        paramsSerializer: (param) =>
          qs.stringify(param, { arrayFormat: 'repeat' }),
        ...config
      })
  },
  serviceConfig: {
    /**
     * Obtiene la información de evidencias y componentes requeridos por un Ticket
     *
     * @tags ServiceConfig
     * @name GetServiceEvidences
     * @request GET: /api/v1/ServiceConfig/GetServiceEvidencesByTicket/{id}
     * @secure
     */
    GetServiceEvidencesByTicket: (
      ticketId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<EvidenceResponse[]>>({
        url: `/ServiceConfig/GetServiceEvidencesByTicket/${+ticketId}`,
        method: 'GET',
        ...config
      })
  },
  rentalCraneCatalog: {
    /**
     * Obtiene el catálogo de grúas de alquiler para el cierre de un ticket
     *
     * @tags ClosedDetails
     * @name GetRentalCraneCatalogForTicketClose
     * @request GET: /api/v1/ClosedDetails/RentalCraneCatalogForTicketClose
     * @secure
     */
    GetRentalCraneCatalogForTicketClose: (
      params: {
        supplierId?: number;
        plate?: string | number | null | undefined;
        ticketId?: number;
        gserProjectId?: number;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<RentalCraneCatalogForTicketClose[]>
      >({
        url: `/ClosedDetails/RentalCraneCatalogForTicketClose`,
        method: 'GET',
        params,
        ...config
      })
  }
};
